import { useEffect, useState } from "react";
import { BookOpen, ChevronLeft, ChevronRight, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const Page = ({ src, n, side }) => (
  <div className="flex flex-col items-center gap-2 min-w-0">
    {src ? (
      <img data-testid={`interior-page-${n}`} src={src} alt={`Page ${n}`}
        className={`block max-h-[64vh] w-auto bg-white shadow-2xl ${side === "left" ? "rounded-l-sm" : "rounded-r-sm"}`} />
    ) : (
      <div data-testid={`interior-page-empty-${side}`} className="h-[64vh] aspect-[2/3] rounded-sm border border-dashed border-gold-500/15 flex items-center justify-center">
        <span className="font-ui text-xs text-slate-500">{side === "left" ? "Inside front cover" : "End of book"}</span>
      </div>
    )}
    <span className="font-ui text-[11px] text-slate-500">{n ? `Page ${n}` : "\u00a0"}</span>
  </div>
);

const Spread = ({ s }) => (
  <div data-testid="interior-spread" className="flex justify-center items-start">
    <Page src={s.images[0]} n={s.pages[0]} side="left" />
    <div className="w-px self-stretch bg-gradient-to-b from-transparent via-black/60 to-transparent mb-6" />
    <Page src={s.images[1]} n={s.pages[1]} side="right" />
  </div>
);

export const InteriorPreview = ({ book, trims, trimKey }) => {
  const [open, setOpen] = useState(false);
  const [trim, setTrim] = useState(trimKey);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [idx, setIdx] = useState(0);
  const group = book.mode === "children" ? "children" : "book";
  const options = trims.filter((t) => t.group === group);

  const valid = options.some((t) => t.key === trimKey) ? trimKey : options[0]?.key || "";
  const show = () => { setTrim(valid); setOpen(true); };
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setData(null); setErr(""); setIdx(0);
    api.interiorPreview(book.id, trim)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e?.response?.data?.detail || "Preview failed"));
    return () => { alive = false; };
  }, [open, trim, book.id]);

  const spreads = data?.spreads || [];
  const cur = spreads[idx];
  return (
    <>
      <button data-testid="reader-print-preview-button" onClick={show}
        className="flex items-center gap-2 font-ui text-sm text-slate-200 border border-gold-500/12 hover:border-gold-400/60 rounded-lg px-3 py-2 transition-colors">
        <BookOpen className="w-4 h-4" /> Print preview
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] bg-navy-950 border-gold-500/20 flex flex-col" data-testid="interior-preview-dialog">
          <DialogHeader>
            <div className="flex flex-wrap items-end gap-4 pr-8">
              <div className="mr-auto">
                <DialogTitle className="font-display text-2xl">Print interior preview</DialogTitle>
                <DialogDescription className="font-read text-sm text-slate-400">
                  Rendered from the real print PDF: what KDP will print, with bleed shown on the outer edges.
                </DialogDescription>
              </div>
              <select data-testid="interior-trim-select" value={trim} onChange={(e) => setTrim(e.target.value)}
                className="font-ui text-sm text-slate-200 bg-navy-900 border border-gold-500/12 hover:border-gold-400/60 rounded-lg px-2.5 py-2 outline-none cursor-pointer">
                {(options.length ? options : [{ key: trim, label: trim }]).map((t) => <option key={t.key} value={t.key} className="bg-navy-900">{t.label}</option>)}
              </select>
            </div>
          </DialogHeader>
          {!data && !err && (
            <div data-testid="interior-preview-loading" className="flex-1 flex items-center justify-center gap-2 text-slate-400 font-ui text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Typesetting the print interior at this trim…
            </div>
          )}
          {err && <p data-testid="interior-preview-error" className="flex items-center gap-2 text-amber-300 font-ui text-sm"><AlertTriangle className="w-4 h-4" /> {err}</p>}
          {cur && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {spreads.map((s, i) => (
                  <button key={i} data-testid={`interior-spread-tab-${i}`} onClick={() => setIdx(i)}
                    className={`font-ui text-xs font-medium rounded-full px-4 py-1.5 transition-colors ${i === idx ? "bg-gold-400 text-stone-950" : "text-slate-300 border border-gold-500/12 hover:border-gold-400/60"}`}>{s.label}</button>
                ))}
                <span data-testid="interior-preview-meta" className="ml-auto font-ui text-xs text-slate-400">
                  {data.page_count} page{data.page_count === 1 ? "" : "s"} · trim {data.trim_in?.[0]} × {data.trim_in?.[1]} in
                </span>
              </div>
              <div className="flex-1 flex items-center gap-4 min-h-0">
                <button data-testid="interior-prev" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="p-2 rounded-full border border-gold-500/12 text-slate-300 disabled:opacity-30 hover:border-gold-400/60 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
                <div className="flex-1 min-w-0"><Spread s={cur} /></div>
                <button data-testid="interior-next" disabled={idx === spreads.length - 1} onClick={() => setIdx(idx + 1)} className="p-2 rounded-full border border-gold-500/12 text-slate-300 disabled:opacity-30 hover:border-gold-400/60 transition-colors"><ChevronRight className="w-5 h-5" /></button>
              </div>
              {cur.note && <p data-testid="interior-spread-note" className="text-center font-read text-sm text-slate-400 mt-3">{cur.note}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
